import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  CardMedia,
  CircularProgress,
} from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Parse from "parse";

export const MarketingCarousel = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMarketingImages();
    
    // Auto-advance carousel every 5 seconds (move by 2 images)
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        if (images.length === 0) return 0;
        const nextIndex = prevIndex + 2;
        return nextIndex >= images.length ? 0 : nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  const fetchMarketingImages = async () => {
    try {
      const result = await Parse.Cloud.run("getMarketingImages");
      setImages(result || []);
    } catch (error) {
      console.error("Error fetching marketing images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex - 2;
      if (newIndex < 0) {
        // Go to last pair
        const lastPairIndex = Math.floor((images.length - 1) / 2) * 2;
        return lastPairIndex;
      }
      return newIndex;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex + 2;
      return newIndex >= images.length ? 0 : newIndex;
    });
  };

  if (loading) {
    return null;
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        position: "relative",
      }}
    >
      <Box position="relative">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: images.length === 1 ? "1fr" : "1fr 1fr",
              gap: 1,
            }}
          >
            <CardMedia
              component="img"
              
              image={images[currentIndex]?.imageUrl}
              alt={`Marketing image ${currentIndex + 1}`}
              sx={{
                objectFit: "contain",
                borderRadius: "8px",
                backgroundColor: "#1a1a1a",
                margin: 0,
                display: "block",
              }}
            />
            {images[currentIndex + 1] && (
              <CardMedia
                component="img"
                
                image={images[currentIndex + 1]?.imageUrl}
                alt={`Marketing image ${currentIndex + 2}`}
                sx={{
                  objectFit: "contain",
                  borderRadius: "8px",
                  backgroundColor: "#1a1a1a",
                  margin: 0,
                  display: "block",
                }}
              />
            )}
          </Box>
          {images.length > 2 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <NavigateNextIcon />
              </IconButton>
              <Box
                position="absolute"
                bottom={8}
                left="50%"
                sx={{ transform: "translateX(-50%)" }}
                display="flex"
                gap={1}
              >
                {Array.from({ length: Math.ceil(images.length / 2) }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: Math.floor(currentIndex / 2) === index ? "white" : "rgba(255, 255, 255, 0.5)",
                      cursor: "pointer",
                    }}
                    onClick={() => setCurrentIndex(index * 2)}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
    </Box>
  );
};
